import { Observable, of } from 'rxjs';
import { Injectable, makeStateKey, TransferState, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { IReview } from '@app/pages/reviews/models/review.interface';

import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { ReviewAggrigatorModel } from '@shared/model/review-aggrigator-model';
import { HttpBaseService } from '@shared/service/base_services/http_base.service';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { statisticsModel } from '@shared/service/http_services/statistics-aggrigator.service';
import { ActivitiesAggrigatorModel } from '@shared/model/activities-aggrigator-model';
import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';

const STATE_KEY_LAST_WEEK_REVIEWS = makeStateKey<
  TServiceResult<PageList<ReviewAggrigatorModel>>
>("last_week_reviews_home");
const STATE_KEY_CHOSEN_REVIEWS = makeStateKey<
  TServiceResult<PageList<ReviewAggrigatorModel>>
>("chosen_reviews_home");
const STATE_KEY_CHOSEN_REVIEWS_FEEDS = makeStateKey<
  TServiceResult<PageList<ReviewAggrigatorModel>>
>("chosen_reviews_feeds");

@Injectable({
  providedIn: "root",
})
export class ReviewsAggrigatorService extends HttpBaseService {
  private transferState = inject(TransferState);

  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, "v1/RevAggr");
  }

  getReviewsOnItem(
    itemId: string,
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http.get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
      `${this.apiUrl}/GetReviewsOnItem/${itemId}/${paramsValues}`
    );
  }

  getReviewsOnWork(
    itemId: string,
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<IReview>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http.get<TServiceResult<PageList<IReview>>>(
      `${this.apiUrl}/GetReviewsOnWork/${itemId}/${paramsValues}`
    );
  }

  getReviewById(
    reviewId: string
  ): Observable<TServiceResult<ReviewAggrigatorModel>> {
    return this.http.get<TServiceResult<ReviewAggrigatorModel>>(
      `${this.apiUrl}/GetReview/${reviewId}`
    );
  }

  getMyReviewOnItem(itemId: string): Observable<TServiceResult<IReview>> {
    return this.http.get<TServiceResult<IReview>>(
      `${this.apiUrl}/GetMyReviewOnItem/${itemId}`
    );
  }

  getGuestReviewOnItem(
    userName: string,
    itemId: string
  ): Observable<TServiceResult<ReviewAggrigatorModel>> {
    return this.http.get<TServiceResult<ReviewAggrigatorModel>>(
      `${this.apiUrl}/GetGuestReviewOnItem/${userName}/${itemId}`
    );
  }

  getMyReviews(
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http.get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
      `${this.apiUrl}/GetMyReviews?${paramsValues}`
    );
  }

  getLatestReviews(
    termFilter: TermFilterModel
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter);
    return this.http
      .get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
        `${this.apiUrl}/GetLatestReviews${paramsValues}`
      )
      .pipe(
        switchMap((res) => {
          this.transferState.set<
            TServiceResult<PageList<ReviewAggrigatorModel>>
          >(STATE_KEY_LAST_WEEK_REVIEWS, res);
          return of(res);
        })
      );
  }

  getLatestReviewsWithUserLiked(
    termFilter: TermFilterModel
  ): Observable<TServiceResult<PageList<IReview>>> {
    const paramsValues = this.ServiceTermFilter(termFilter);
    return this.http.get<TServiceResult<PageList<IReview>>>(
      `${this.apiUrl}/GetLatestReviewsWithUserLiked${paramsValues}`
    );
  }

  getGuestRates(
    userId: string,
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<IReview>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http.get<TServiceResult<PageList<IReview>>>(
      `${this.apiUrl}/GetGuestRates/${userId}${paramsValues}`
    );
  }

  getGuestReviews(
    userId: string,
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http
      .get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
        `${this.apiUrl}/GetGuestReviews/${userId}${paramsValues}`
      )
      .pipe(
        catchError((err) => {
          return of(
            new TServiceResult<PageList<ReviewAggrigatorModel>>(
              new PageList(),
              "",
              null,
              true,
              ""
            )
          );
        })
      );
  }

  getGuestRateReviews(
    userId: string,
    termFilter?: TermFilterModel
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter!);
    return this.http.get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
      `${this.apiUrl}/GetGuestRateReviews/${userId}${paramsValues}`
    );
  }

  getGuestCount(userId: string): Observable<TServiceResult<userCounts>> {
    return this.http
      .get<TServiceResult<userCounts>>(
        `${this.apiUrl}/GetGuestCounts/${userId}`
      )
      .pipe(shareReplay(1));
  }

  getChosenReviews(
    termFilter: TermFilterModel,
    tag = "feed"
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter);
    return this.http
      .get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
        `${this.apiUrl}/GetChosenBookLists/${paramsValues}&tag=${tag}`
      )
      .pipe(
        switchMap((res) => {
          this.transferState.set<
            TServiceResult<PageList<ReviewAggrigatorModel>>
          >(STATE_KEY_CHOSEN_REVIEWS, res);
          return of(res);
        })
      );
  }

  getChosenReviewsWithUserLiked(
    termFilter: TermFilterModel,
    tag = "feed"
  ): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    const paramsValues = this.ServiceTermFilter(termFilter);
    return this.http
      .get<TServiceResult<PageList<ReviewAggrigatorModel>>>(
        `${this.apiUrl}/GetChosenBookListsWithUserLiked/${paramsValues}&tag=${tag}`
      )
      .pipe(
        switchMap((res) => {
          this.transferState.set<
            TServiceResult<PageList<ReviewAggrigatorModel>>
          >(STATE_KEY_CHOSEN_REVIEWS_FEEDS, res);
          return of(res);
        })
      );
  }

  GetGuestReviewOnItem(
    term: string,
    itemId: string
  ): Observable<TServiceResult<ActivitiesAggrigatorModel>> {
    return this.http.get<TServiceResult<ActivitiesAggrigatorModel>>(
      `${this.apiUrl}/GetGuestReviewOnItemV2/${term}/${itemId}`
    );
  }

  getOrderedReviewsOnWork(itemId: string, searchFilter?: SearchFilterModel): Observable<TServiceResult<PageList<IReview>>> {
    return this.http.post<TServiceResult<PageList<IReview>>>(`${this.apiUrl}/GetOrderedReviewsOnWork/${itemId}`, searchFilter)
  }
}

export class userCounts {
  userId?: string;
  rateCount?: number;
  totalCount?: number;
  reviewCount?: number;
  bookListCount?: number;
}
